import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getRepository, getRepositoryBySlug, starRepository, watchRepository, forkRepository, getCollaborators, addCollaborator, archiveRepository } from 'service/ncoreBoardService';
import { useAuth } from 'contexts/AuthContext';
import CodeTab from './components/CodeTab/CodeTab';
import IssuesTab from './components/IssuesTab/IssuesTab';
import PullRequestsTab from './components/PullRequestsTab/PullRequestsTab';
import ReleasesTab from './components/ReleasesTab/ReleasesTab';
import InsightsTab from './components/InsightsTab/InsightsTab';
import WikiTab from './components/WikiTab/WikiTab';
import Toast from './components/Toast/Toast';
import { showToast } from './toastBus';
import styles from './NcoreHubRepo.module.scss';

const TabIcons = {
    code: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/>
        </svg>
    ),
    issues: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
    ),
    pulls: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="18" cy="18" r="3"/><circle cx="6" cy="6" r="3"/>
            <path d="M13 6h3a2 2 0 012 2v7"/><line x1="6" y1="9" x2="6" y2="21"/>
        </svg>
    ),
    releases: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M20.59 13.41l-7.17 7.17a2 2 0 01-2.83 0L2 12V2h10l8.59 8.59a2 2 0 010 2.82z"/>
            <line x1="7" y1="7" x2="7.01" y2="7"/>
        </svg>
    ),
    wiki: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z"/>
            <path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>
        </svg>
    ),
    insights: (
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
            <line x1="6" y1="20" x2="6" y2="14"/>
        </svg>
    ),
};

const TABS = [
    { key: 'code',     label: 'Код' },
    { key: 'issues',   label: 'Задачи' },
    { key: 'pulls',    label: 'Pull Requests' },
    { key: 'releases', label: 'Релизы' },
    { key: 'wiki',     label: 'Wiki' },
    { key: 'insights', label: 'Инсайты' },
];

function Avatar({ user, size = 32 }) {
    return user?.avatar
        ? <img src={user.avatar} alt={user.username} style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover' }} />
        : <div style={{
            width: size, height: size, borderRadius: '50%',
            background: 'linear-gradient(135deg, #9D3EF7, #7c3aed)',
            color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontWeight: 700, fontSize: size * 0.4, flexShrink: 0
          }}>
            {(user?.username || '?')[0].toUpperCase()}
          </div>;
}

export default function NcoreHubRepo() {
    const { owner, repo: repoSlug } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [repo, setRepo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('code');
    const [starred, setStarred] = useState(false);
    const [watched, setWatched] = useState(false);
    const [showCollaborators, setShowCollaborators] = useState(false);
    const [collaborators, setCollaborators] = useState([]);
    const [newCollab, setNewCollab] = useState({ username: '', permission: 'read' });
    const [addingCollab, setAddingCollab] = useState(false);

    useEffect(() => {
        loadRepo();
    }, [owner, repoSlug]);

    async function loadRepo() {
        setLoading(true);
        setError(null);
        try {
            const data = await getRepositoryBySlug(owner, repoSlug);
            setRepo(data);
            setStarred(data.is_starred || false);
            setWatched(data.is_watched || false);
        } catch (err) {
            setError('Репозиторий не найден или недоступен');
        }
        setLoading(false);
    }

    async function handleStar() {
        if (!user) return;
        try {
            await starRepository(repo.id);
            setStarred(s => {
                const next = !s;
                setRepo(r => ({ ...r, stars_count: r.stars_count + (next ? 1 : -1) }));
                return next;
            });
        } catch {}
    }

    async function handleWatch() {
        if (!user) return;
        try {
            await watchRepository(repo.id);
            setWatched(w => !w);
        } catch {}
    }

    async function handleFork() {
        if (!user) return;
        if (isOwner) {
            showToast('Нельзя форкать собственный репозиторий', 'warning');
            return;
        }
        try {
            const forked = await forkRepository(repo.id);
            showToast('Репозиторий успешно форкнут', 'success');
            navigate(`/ncoreboard/${user.username}/${forked.slug}`);
        } catch (err) { showToast(err.message || 'Ошибка при форке', 'error'); }
    }

    async function loadCollaborators() {
        try {
            const data = await getCollaborators(repo.id);
            setCollaborators(data.results || data);
        } catch {}
    }

    async function handleAddCollab(e) {
        e.preventDefault();
        setAddingCollab(true);
        try {
            await addCollaborator(repo.id, newCollab);
            setNewCollab({ username: '', permission: 'read' });
            loadCollaborators();
        } catch (err) { showToast(err.message || 'Ошибка при добавлении участника', 'error'); }
        setAddingCollab(false);
    }

    const isOwner = user && (user.username === owner || user?.id === repo?.owner?.id);

    if (loading) {
        return (
            <div className={styles.loadingPage}>
                <div className={styles.loadingSpinner} />
                <span>Загрузка репозитория...</span>
            </div>
        );
    }

    if (error || !repo) {
        return (
            <div className={styles.errorPage}>
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.2" style={{ color: 'rgba(255,255,255,0.2)' }}>
                    <circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/>
                </svg>
                <h2>{error || 'Репозиторий не найден'}</h2>
                <button className={styles.backBtn} onClick={() => navigate('/ncoreboard')}>← Назад к NcoreHub</button>
            </div>
        );
    }

    return (
        <div className={styles.page}>
            {/* Repo header */}
            <div className={styles.repoHeader}>
                <div className={styles.headerInner}>
                    {/* Breadcrumb */}
                    <div className={styles.breadcrumb}>
                        <button className={styles.breadcrumbLink} onClick={() => navigate('/ncoreboard')}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                <g transform="translate(12,12)">
                                    <path d="M0-2Q1.5-4,0-6Q-1.5-4,0-2Z"/>
                                    <path d="M0-2Q1.5-4,0-6Q-1.5-4,0-2Z" transform="rotate(72)"/>
                                    <path d="M0-2Q1.5-4,0-6Q-1.5-4,0-2Z" transform="rotate(144)"/>
                                    <path d="M0-2Q1.5-4,0-6Q-1.5-4,0-2Z" transform="rotate(216)"/>
                                    <path d="M0-2Q1.5-4,0-6Q-1.5-4,0-2Z" transform="rotate(288)"/>
                                    <circle cx="0" cy="0" r="1.4"/>
                                    <path d="M0 1.5Q-0.6 3.5,-0.6 6L0.6 6Q0.6 3.5,0 1.5Z"/>
                                </g>
                            </svg>
                            NcoreHub
                        </button>
                        <span className={styles.breadcrumbSep}>/</span>
                        <button className={styles.breadcrumbLink} onClick={() => navigate(`/ncoreboard?filter=user&u=${owner}`)}>
                            {owner}
                        </button>
                        <span className={styles.breadcrumbSep}>/</span>
                        <span className={styles.breadcrumbCurrent}>{repo.slug}</span>
                        {repo.is_private && <span className={styles.privateBadge}>Приватный</span>}
                        {repo.is_archived && <span className={styles.archiveBadge}>Архив</span>}
                        {repo.forked_from && <span className={styles.forkedBadge}>Форк</span>}
                    </div>

                    {/* Title + description */}
                    <div className={styles.titleRow}>
                        <div className={styles.repoAvatar}>
                            <Avatar user={repo.owner} size={40} />
                        </div>
                        <div className={styles.titleInfo}>
                            <h1 className={styles.repoTitle}>{repo.name}</h1>
                            {repo.description && <p className={styles.repoDesc}>{repo.description}</p>}
                            {repo.forked_from && (
                                <p className={styles.forkFrom}>
                                    Форк от <button className={styles.forkLink} onClick={() => navigate(`/ncoreboard/${repo.forked_from_owner}/${repo.forked_from_slug}`)}>
                                        {repo.forked_from_owner}/{repo.forked_from_slug}
                                    </button>
                                </p>
                            )}
                        </div>
                    </div>

                    {/* Topics */}
                    {repo.topics?.length > 0 && (
                        <div className={styles.topics}>
                            {repo.topics.map((t, i) => <span key={i} className={styles.topic}>{t}</span>)}
                        </div>
                    )}

                    {/* Action buttons */}
                    <div className={styles.actions}>
                        {user && (
                            <>
                                <button
                                    className={`${styles.actionBtn} ${watched ? styles.actionActive : ''}`}
                                    onClick={handleWatch}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                                    </svg>
                                    {watched ? 'Наблюдаю' : 'Наблюдать'}
                                </button>
                                <button
                                    className={`${styles.actionBtn} ${starred ? styles.actionActive : ''}`}
                                    onClick={handleStar}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill={starred ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2">
                                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                                    </svg>
                                    {starred ? 'Снять звезду' : 'Звезда'} <span className={styles.forkCount}>{repo.stars_count}</span>
                                </button>
                                {!isOwner && (
                                <button className={styles.actionBtn} onClick={handleFork}>
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                        <path d="M6 3a3 3 0 11-1 5.83V13a3 3 0 002 2.83V13.17a3 3 0 111 0V15.83a3 3 0 10.87.07l.13-.07V8.83A3 3 0 006 3zm0 2a1 1 0 110 2 1 1 0 010-2zm12 0a1 1 0 110 2 1 1 0 010-2zm-6 12a1 1 0 110 2 1 1 0 010-2z"/>
                                    </svg>
                                    Форк <span className={styles.forkCount}>{repo.forks_count}</span>
                                </button>
                                )}
                                {isOwner && (
                                    <button
                                        className={styles.actionBtn}
                                        onClick={() => { setShowCollaborators(v => { if (!v) loadCollaborators(); return !v; }); }}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/>
                                            <path d="M23 21v-2a4 4 0 00-3-3.87"/><path d="M16 3.13a4 4 0 010 7.75"/>
                                        </svg>
                                        Участники
                                    </button>
                                )}
                            </>
                        )}
                    </div>

                    {/* Collaborators panel */}
                    {showCollaborators && isOwner && (
                        <div className={styles.collabPanel}>
                            <h4 className={styles.collabTitle}>Участники репозитория</h4>
                            <div className={styles.collabList}>
                                {/* owner */}
                                <div className={styles.collabRow}>
                                    <Avatar user={repo.owner} size={28} />
                                    <span className={styles.collabName}>{repo.owner?.username}</span>
                                    <span className={styles.collabPermission} style={{ color: '#9D3EF7' }}>Владелец</span>
                                </div>
                                {collaborators.map(c => (
                                    <div key={c.id} className={styles.collabRow}>
                                        <Avatar user={c.user} size={28} />
                                        <span className={styles.collabName}>{c.user?.username}</span>
                                        <span className={styles.collabPermission}>{c.permission}</span>
                                    </div>
                                ))}
                            </div>
                            <form className={styles.addCollabForm} onSubmit={handleAddCollab}>
                                <input
                                    className={styles.collabInput}
                                    placeholder="Имя пользователя"
                                    value={newCollab.username}
                                    onChange={e => setNewCollab(p => ({ ...p, username: e.target.value }))}
                                    required
                                />
                                <select
                                    className={styles.collabSelect}
                                    value={newCollab.permission}
                                    onChange={e => setNewCollab(p => ({ ...p, permission: e.target.value }))}
                                >
                                    <option value="read">Чтение</option>
                                    <option value="write">Запись</option>
                                    <option value="admin">Админ</option>
                                </select>
                                <button type="submit" className={styles.addCollabBtn} disabled={addingCollab}>
                                    {addingCollab ? '...' : 'Добавить'}
                                </button>
                            </form>
                        </div>
                    )}
                </div>
            </div>

            {/* Tab nav */}
            <div className={styles.tabBar}>
                <div className={styles.tabBarInner}>
                    {TABS.map(tab => (
                        <button
                            key={tab.key}
                            className={`${styles.tab} ${activeTab === tab.key ? styles.tabActive : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            <span className={styles.tabIcon}>{TabIcons[tab.key]}</span>
                            {tab.label}
                            {tab.key === 'issues' && repo.open_issues_count > 0 && (
                                <span className={styles.tabBadge}>{repo.open_issues_count}</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab content */}
            <div className={styles.content}>
                <div className={styles.contentInner}>
                    {activeTab === 'code' && <CodeTab repo={repo} />}
                    {activeTab === 'issues' && <IssuesTab repo={repo} isOwner={isOwner} />}
                    {activeTab === 'pulls' && <PullRequestsTab repo={repo} isOwner={isOwner} />}
                    {activeTab === 'releases' && <ReleasesTab repo={repo} isOwner={isOwner} />}
                    {activeTab === 'wiki' && <WikiTab repo={repo} isOwner={isOwner} />}
                    {activeTab === 'insights' && <InsightsTab repo={repo} />}
                </div>
            </div>
            <Toast />
        </div>
    );
}
